<template>
    <div class="autocomplete" :class="{running: running}">
        <slot name="input">
            <input type="search"
                   ref="input"
                   :class="[{'has-result': results.length > 0}, inputClass]"
                   :value="value"
                   :placeholder="placeholder"
                   :disabled="inputDisabled"
                   @input="$emit('input', $refs.input.value)"
                   @keypress.enter="onKeyEnterPress"
                   @keyup="search"
                   @keydown.down="moveCursor(false)"
                   @keydown.up="moveCursor(true)"
                   @focus="inputFocus">
        </slot>
        <slot name="list">
            <ul v-if="results && results.length > 0" class="autocomplete-list list-n p-0 m-0" :style="listStyle">
                <li v-for="(val, idx) in results"
                    @mouseover="selectedIndex = idx"
                    @mousedown="selectItem"
                    :key="idx"
                    :class="{active: idx === selectedIndex}"
                    v-html="itemPresentation(val, $refs.input.value)"></li>
            </ul>
        </slot>
    </div>
</template>

<script>
    import {isString} from "../../utils/Utils";

    export default {
        name: "Autocomplete",
        props: {
            value: {
                type: String,
                default: ''
            },
            itemPresentation: {
                type: Function,
                default: val => val
            },
            itemValuePresentation: {
                type: Function,
                default: val => val
            },
            url: {
                type: String
            },
            placeholder: {
                type: String
            },
            onSelectItem: {
                type: Function,
                default: obj => obj
            },
            inputClass: {
                type: String | Object | Array,
                default: 'form-control'
            },
            inputDisabled: {
                type: [Boolean, String],
                default: false,
                validator: val => {
                    if (isString(val)) return /(true|false)/.test(val.trim().toLowerCase());
                    return true
                }
            }
        },
        data() {
            return {
                running: false,
                results: [],
                listStyle: {},
                selectedIndex: -1,
                handler: null,
                lastResult: []
            }
        },
        methods: {
            inputFocus() {
                this.selectedIndex = -1;
                this.listStyle = {
                    width: `${this.$refs.input.offsetWidth}px`
                };

                if (this.lastResult) this.results = this.lastResult;
            },
            moveCursor(isUp) {
                const len = this.results.length;
                if (len > 0) {
                    let idx = parseInt(this.selectedIndex);
                    idx += isUp ? -1 : 1;

                    if (idx < 0) idx = len - 1;
                    else if (idx >= len) idx = 0;

                    this.selectedIndex = idx;
                }
            },
            search(event) {

                if (/(Enter|Arrow*)/gi.test(event.key)) return true;

                if (/Escape/gi.test(event.key)) {
                    this.results = [];
                    this.lastResult = null;
                    return false;
                }

                if (this.handler) clearTimeout(this.handler);

                const query = this.$refs.input.value.trim();

                this.handler = setTimeout(() => {
                    this.running = true;
                    this.$appHttp.get(`${this.url}${encodeURIComponent(query)}`)
                        .then(resp => this.results = resp.data)
                        .catch(err => {
                            this.$snotify.error('Ops... Aconteceu algum problema na consulta', this.$appStorage.getUser().name);
                            console.error('Autocomplete error', err);
                        })
                        .finally(() => this.running = false)
                }, 500);
            },
            onKeyEnterPress(e) {
                e.preventDefault();
                e.cancelBubble = true;

                this.selectItem();

                return false;
            },
            selectItem() {
                if (this.selectedIndex > -1 && this.selectedIndex <= this.results.length) {
                    const item = this.results[this.selectedIndex];

                    const div = document.createElement('div');
                    div.innerHTML = this.itemPresentation(item);
                    const val = div.textContent || div.innerText || '';

                    this.onSelectItem(item, val);

                    this.$emit('input', this.itemValuePresentation(item));

                    this.lastResult = Object.assign([], this.results);
                    this.results = [];
                }
            }
        },
        mounted() {
            this.inputFocus();
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../assets/css/variables";
    .autocomplete {
        position: relative;
        width: 100%;
        &.running::after {
            position: absolute;
            content: '';
            background: linear-gradient(90deg, #1AB39A 50%, #FFF 50%);
            right: 5px;
            top: 25%;
            width: 1em;
            height: 1em;
            border-radius: 50%;
            animation: .5s rotate-360 infinite forwards;
        }

        input {
            width: 100%;
            &.has-result:focus {
                border-bottom: none;
                border-radius: 3px 3px 0 0;
                background: #fff;
                color: #000;
                box-shadow: 0 15px 25px rgba(0, 0, 0, .6);

                + ul {
                    animation: listIn .2s linear;
                    animation-fill-mode: forwards;
                }
            }
        }

        ul {
            position: absolute;
            transition: background-color .2s linear, height .2s ease-in-out, opacity .2s linear;
            color: #000;
            box-sizing: border-box;
            padding-inline-start: unset;
            background-color: rgba(255, 255, 255, .2);
            border-bottom: 1px solid $theme_color_one;
            border-left: 1px solid $theme_color_one;
            border-right: 1px solid $theme_color_one;
            border-radius: 0 0 3px 3px;
            z-index: 10;
            height: 0;
            overflow: hidden;
            opacity: 0;

            li {
                padding: 5px;
                cursor: pointer;
                overflow-x: hidden;
                background-color: #fff;
                transition: background-color .2s ease;
                font-size: .8em;

                &.active {
                    background-color: $theme_color_one;
                    color: $theme_color_four;
                }
            }
        }
    }

    @keyframes listIn {
        to {
            background-color: #fff;
            border-bottom: 1px solid $theme_color_one;
            border-left: 1px solid $theme_color_one;
            border-right: 1px solid $theme_color_one;
            height: auto;
            opacity: 1;
            box-shadow: 0 15px 25px rgba(0, 0, 0, .6);
        }
    }
</style>
